import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import InformationBanner from '@/components/InformationBanner';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { LocationViewChoices, PricingPlan, WIDGET_SETTING_CHOICE } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { handleVisibleBanner, isOpenDashboardInstructionSchedulingWidgetSelector } from '@/redux/slice/banner.slice';
import { handleLocationTab } from '@/redux/slice/tab.slice';
import {
  ActionList,
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  InlineGrid,
  InlineStack,
  Popover,
} from '@shopify/polaris';
import { ExternalSmallIcon } from '@shopify/polaris-icons';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BoldTextDiv, ButtonStyled, FullHeightDiv, FullWidthDiv } from '../../styled';

interface SchedulingWidgetProps {
  currentPlan?: string;
  quota: number;
  used: number;
  shop: string | null;
  isActiveEmbed?: boolean;
  appEmbedId?: string;
  themeId?: string;
  position?: string[];
  hasEnableMethods?: {
    isEnablePickup: boolean;
    isEnableDelivery: boolean;
    isEnableShipping: boolean;
  };
}

export const SchedulingWidgetInfor = ({
  currentPlan,
  quota,
  used,
  shop,
  isActiveEmbed,
  appEmbedId,
  themeId,
  position,
  hasEnableMethods,
}: SchedulingWidgetProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('dashboard');
  const isOpenDashboardInstructionSchedulingWidget = useSelector(isOpenDashboardInstructionSchedulingWidgetSelector);
  const navigate = useNavigate();

  const [popoverActive, setPopoverActive] = useState(false);

  const togglePopoverActive = useCallback(() => setPopoverActive((popoverActive) => !popoverActive), []);

  const activator = (
    <Button onClick={togglePopoverActive} disclosure>
      {t('scheduleWigetInfor.customize.title')}
    </Button>
  );

  const handleNavigate = useCallback(
    (choice: number) => {
      dispatch(handleLocationTab({ key: 'widgetTab', value: choice }));
      navigate(PATH.WIDGET);
    },
    [dispatch, navigate],
  );

  const handleEmbedApp = useCallback(() => {
    if (!appEmbedId) {
      return;
    }
    const appIdPath = encodeURIComponent(`${appEmbedId}/app-embed-block.`);
    const url = new URL(`https://${shop}/admin/themes/${themeId}/editor`);
    url.searchParams.set('context', 'apps');
    url.searchParams.set('template', 'cart');
    url.searchParams.set('activateAppId', appIdPath);

    window.open(decodeURIComponent(url.toString()), '_blank');
  }, [appEmbedId, shop, themeId]);

  const fulfillmentStatusData = useMemo(
    () => [
      {
        id: 0,
        content: t('scheduleWigetInfor.fulfillmentStatus.shipping'),
        status: hasEnableMethods?.isEnableShipping,
        handleClick: () => navigate(PATH.SHIPPING),
      },
      {
        id: 1,
        content: t('scheduleWigetInfor.fulfillmentStatus.delivery'),
        path: PATH.LOCATIONS,
        status: hasEnableMethods?.isEnableDelivery,
        handleClick: () => {
          if (hasEnableMethods?.isEnableDelivery) {
            dispatch(handleLocationTab({ key: 'locationTab', value: LocationViewChoices.DELIVERY }));
            navigate(PATH.LOCATIONS);
          } else {
            dispatch(handleLocationTab({ key: 'locationTab', value: LocationViewChoices.ALL }));
            navigate(PATH.LOCATIONS);
          }
        },
      },
      {
        id: 2,
        content: t('scheduleWigetInfor.fulfillmentStatus.pickup'),
        status: hasEnableMethods?.isEnablePickup,
        handleClick: () => {
          if (hasEnableMethods?.isEnablePickup) {
            dispatch(handleLocationTab({ key: 'locationTab', value: LocationViewChoices.PICKUP }));
            navigate(PATH.LOCATIONS);
          } else {
            dispatch(handleLocationTab({ key: 'locationTab', value: LocationViewChoices.ALL }));
            navigate(PATH.LOCATIONS);
          }
        },
      },
    ],
    [
      dispatch,
      hasEnableMethods?.isEnableDelivery,
      hasEnableMethods?.isEnablePickup,
      hasEnableMethods?.isEnableShipping,
      navigate,
      t,
    ],
  );

  const handleRenderPosition = useCallback((): string => {
    if (position) {
      const hasCart = position.includes('cart');
      const hasProduct = position.includes('product');

      if (hasCart && hasProduct) return t('scheduleWigetInfor.position.both');
      if (hasCart) return t('scheduleWigetInfor.position.cart');
      if (hasProduct) return t('scheduleWigetInfor.position.product');

      return '';
    } else return '';
  }, [position, t]);

  const isActiveWidget = useMemo(() => {
    const hasAnyMethodEnabled =
      hasEnableMethods?.isEnableDelivery || hasEnableMethods?.isEnablePickup || hasEnableMethods?.isEnableShipping;

    return Boolean(hasAnyMethodEnabled && isActiveEmbed);
  }, [hasEnableMethods, isActiveEmbed]);

  return (
    <>
      <Grid columns={{ lg: 12, xs: 6 }} gap={{ xs: '400' }}>
        <Grid.Cell columnSpan={{ lg: 8, md: 3, xs: 6 }}>
          <FullHeightDiv>
            <Card>
              <BlockStack gap="200">
                <InformationBanner
                  isVisible={isOpenDashboardInstructionSchedulingWidget}
                  onDismiss={() =>
                    dispatch(
                      handleVisibleBanner({
                        key: 'isOpenDashboardInstructionSchedulingWidget',
                        value: false,
                      }),
                    )
                  }
                >
                  <RegularText>
                    Please be awared that to show widget: you need to <BoldSmallText as="span">embed app</BoldSmallText> and{' '}
                    <BoldSmallText as="span">activate at least 1 scheduling rule.</BoldSmallText>
                  </RegularText>
                </InformationBanner>
                <FullWidthDiv className="full-height">
                  <InlineStack align="space-between" blockAlign="start">
                    <BlockStack align="space-between" gap={'300'}>
                      <div className="not-full-inline">
                        <InlineStack align="space-between" blockAlign="center" gap={'200'}>
                          <InlineGrid columns={2} gap="200">
                            <BoldText>{t('scheduleWigetInfor.schedulingWidget')}</BoldText>
                            <BoldTextDiv>
                              <Badge progress="complete" tone={isActiveWidget ? 'success' : undefined}>
                                {isActiveWidget
                                  ? t('scheduleWigetInfor.schedulingWidgetActive')
                                  : t('scheduleWigetInfor.schedulingWidgetInactive')}
                              </Badge>
                            </BoldTextDiv>
                          </InlineGrid>
                          <div className="not-full-inline">
                            <InlineStack gap="200">
                              <Popover
                                active={popoverActive}
                                activator={activator}
                                autofocusTarget="first-node"
                                onClose={togglePopoverActive}
                              >
                                <ActionList
                                  actionRole="menuitem"
                                  items={[
                                    { content: 'Edit position', onAction: () => handleNavigate(WIDGET_SETTING_CHOICE.POSITION) },
                                    {
                                      content: 'Edit text & design',
                                      onAction: () => handleNavigate(WIDGET_SETTING_CHOICE.DESIGN),
                                    },
                                  ]}
                                />
                              </Popover>
                              {isActiveEmbed ? (
                                <ButtonStyled>
                                  <Button
                                    variant="primary"
                                    icon={ExternalSmallIcon}
                                    onClick={() => window.open(`https://${config.shop}`)}
                                  >
                                    {t('scheduleWigetInfor.live')}
                                  </Button>
                                </ButtonStyled>
                              ) : (
                                <Button variant="primary" onClick={handleEmbedApp}>
                                  {t('scheduleWigetInfor.embedBtn')}
                                </Button>
                              )}
                            </InlineStack>
                          </div>
                        </InlineStack>
                      </div>
                      <RegularText>
                        {t('scheduleWigetInfor.position.title')} <BoldText as="span">{handleRenderPosition()}</BoldText>
                      </RegularText>
                      <RegularText>
                        Plan: {currentPlan?.toUpperCase()}{' '}
                        <BoldText as="span">
                          ({used}/
                          {currentPlan === PricingPlan.Enterprise
                            ? 'unlimited orders per month'
                            : `${quota} orders limit per month`}
                          )
                        </BoldText>
                      </RegularText>
                    </BlockStack>
                  </InlineStack>
                </FullWidthDiv>
              </BlockStack>
            </Card>
          </FullHeightDiv>
        </Grid.Cell>
        <Grid.Cell columnSpan={{ lg: 4, md: 3, xs: 6 }}>
          <FullHeightDiv>
            <Card padding={'0'}>
              <BlockStack>
                <div className="not-fullHeight-box">
                  <Box padding={'300'}>
                    <BoldText>{t('scheduleWigetInfor.fulfillmentStatus.title')}</BoldText>
                  </Box>
                </div>
                <Divider borderColor="border" />
                <Box padding={'400'}>
                  <BlockStack gap="300" align="space-between">
                    {fulfillmentStatusData.map((item) => (
                      <div className="not-full-inline" key={item.id}>
                        <InlineStack align="space-between">
                          <Button variant="plain" onClick={item.handleClick}>
                            {item.content}
                          </Button>
                          <Badge tone={item.status ? 'success' : 'attention'}>
                            {item.status
                              ? t('scheduleWigetInfor.fulfillmentStatus.on')
                              : t('scheduleWigetInfor.fulfillmentStatus.off')}
                          </Badge>
                        </InlineStack>
                      </div>
                    ))}
                  </BlockStack>
                </Box>
              </BlockStack>
            </Card>
          </FullHeightDiv>
        </Grid.Cell>
      </Grid>
    </>
  );
};
